import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getAllParts, deletePart } from "@/services/partsService";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Pencil, Trash2, Package, Eye } from "lucide-react";
import { toast } from "sonner";
import { queryKeys } from "@/lib/queryKeys";

export function Parts() {
  const queryClient = useQueryClient();
  const { data: parts = [], isLoading: loading } = useQuery({
    queryKey: queryKeys.parts,
    queryFn: getAllParts,
  });
  const [search, setSearch] = useState("");

  const filtered = parts.filter(
    (p) =>
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id: string) => {
    if (!confirm("¿Eliminar este repuesto?")) return;
    try {
      await deletePart(id);
      toast.success("Repuesto eliminado");
      await queryClient.invalidateQueries({ queryKey: queryKeys.parts });
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Repuestos</h1>
          <p className="text-muted-foreground">Lista y gestión de repuestos</p>
        </div>
        <Button asChild>
          <Link to="/parts/new" className="gap-2">
            <Plus className="h-4 w-4" />
            Nuevo repuesto
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-sm"
            />
          </div>
          {loading ? (
            <p className="text-muted-foreground">Cargando…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[64px]">Imagen</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Marca</TableHead>
                  <TableHead>Precio</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead className="w-[130px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      {(p.images?.length ?? 0) > 0 ? (
                        <img
                          src={p.images![0]}
                          alt=""
                          className="h-10 w-10 rounded object-cover border border-border"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded border border-dashed border-muted-foreground/30 bg-muted/50 text-muted-foreground">
                          <Package className="h-5 w-5 opacity-50" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link to={`/parts/${p.id}`} className="hover:text-primary hover:underline transition-colors">
                        {p.name}
                      </Link>
                    </TableCell>
                    <TableCell>{p.sku ?? "—"}</TableCell>
                    <TableCell>{(p.category as { name?: string })?.name ?? "—"}</TableCell>
                    <TableCell>{(p.brand as { name?: string })?.name ?? "—"}</TableCell>
                    <TableCell>{p.price != null ? `${p.price} ${p.currency ?? "USD"}` : "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{p.stock ?? "—"}</span>
                        {p.stock != null && p.stock <= 0 && (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Agotado</Badge>
                        )}
                        {p.stock != null && p.stock > 0 && p.stock <= 5 && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-300">Bajo</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" asChild title="Ver detalles">
                          <Link to={`/parts/${p.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" asChild title="Editar">
                          <Link to={`/parts/edit/${p.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)} title="Eliminar">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!loading && filtered.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">No hay repuestos</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
